import { useCallback, useEffect, useState } from 'react';
import { isExpiringSoon } from '../utils/decodeToken';

const WARN_WITHIN_MS = 24 * 60 * 60 * 1000; // 24h

/**
 * Lightweight, "poor man's" session-expiry warning (Phase 9, R2.2).
 *
 * Reads the stored `session_token` and reports whether it expires within ~24h.
 * The check runs on mount and whenever the tab regains focus — no timers, no
 * polling. Dismissal is sticky for the current app session (resets on reload /
 * next focus-after-dismiss is suppressed until the token changes is NOT tracked;
 * dismissal simply hides it until the next mount). The hard expiry case (actual
 * 401) is handled separately by the data client, not here.
 */
export function useSessionExpiry(): {
  expiringSoon: boolean;
  dismiss: () => void;
} {
  const [expiringSoon, setExpiringSoon] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const check = useCallback(() => {
    let token: string | null = null;
    try {
      token = localStorage.getItem('session_token');
    } catch {
      token = null;
    }
    setExpiringSoon(isExpiringSoon(token, WARN_WITHIN_MS));
  }, []);

  useEffect(() => {
    check();
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [check]);

  const dismiss = useCallback(() => setDismissed(true), []);

  return { expiringSoon: expiringSoon && !dismissed, dismiss };
}
